import { Component, OnInit } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../services/api.service';
import { ActivatedRoute } from '@angular/router';
import { RouterModule } from '@angular/router';
import { ToastService } from '../components/toast.service';

@Component({
  standalone: true,
  selector: 'app-settings',
  imports: [CommonModule, FormsModule, RouterModule],
  styles: [
    `
    .card { border-radius: 12px; }
    .card-header { background: transparent; border-bottom: 0; }
    .tile {
      border: 1px solid var(--bs-border-color);
      border-radius: 10px;
      padding: .75rem 1rem;
      display: flex; align-items: center; justify-content: space-between;
      gap: .75rem;
    }
    .tile + .tile { margin-top: .5rem; }
    .muted { color: var(--bs-secondary-color); }
    .labels-list { display: flex; flex-direction: column; gap: .5rem; }
    .labels-item { display: flex; align-items: center; gap: .75rem; }
    .labels-num { width: 28px; height: 28px; border-radius: 50%; background: var(--bs-tertiary-bg); color: var(--bs-body-color); display: inline-flex; align-items: center; justify-content: center; font-weight: 600; border: 1px solid var(--bs-border-color); }
    .labels-item .form-control { flex: 1; }
    `
  ],
  template: `
<div class="d-flex align-items-center justify-content-between mb-3">
  <button class="btn btn-sm btn-outline-secondary" (click)="goBack()">←<span class="d-none d-lg-inline"> Zurück</span></button>
  <h5 class="mb-0">Einstellungen</h5>
  <span></span>
</div>

<div class="row g-3">
  <div class="col-12 col-lg-6">
    <div class="card">
      <div class="card-header d-flex align-items-center justify-content-between">
        <strong>🔗 Eigene Bewertungen teilen</strong>
      </div>
      <div class="card-body">
        <div class="mb-2">
          <label class="form-label">Festival wählen</label>
          <select class="form-select" [(ngModel)]="festivalId">
            <option [ngValue]="''" disabled>Bitte Festival wählen</option>
            <option *ngFor="let f of festivals" [ngValue]="f.id">{{ f.name }} ({{ f.id }})</option>
          </select>
        </div>
        <div class="mb-2">
          <label class="form-label">Alias (optional)</label>
          <div class="input-group">
            <span class="input-group-text">🏷️</span>
            <input class="form-control" [(ngModel)]="alias">
          </div>
          <div class="form-text">Leer lassen, um eine zufällige Kennung zu generieren.</div>
        </div>
        <div class="form-check mb-2">
          <input class="form-check-input" type="checkbox" [(ngModel)]="includeNotes" id="incl">
          <label class="form-check-label" for="incl">Notizen mit freigeben</label>
        </div>
        <button class="btn btn-primary" (click)="createShare()" [disabled]="!festivalId || festivalSharesCount >= 4">Freigeben</button>
        <span class="ms-2 small text-muted" *ngIf="festivalSharesCount > 0">Freigaben für dieses Festival: {{ festivalSharesCount }}/4</span>
        <span class="ms-2 badge text-bg-warning" *ngIf="festivalSharesCount >= 4">Limit erreicht</span>
        <div class="alert alert-success mt-2 py-2 px-3 d-flex align-items-center justify-content-between" *ngIf="shareToken">
          <span>Freigabe-Kennung: <strong>{{ shareToken }}</strong></span>
          <button class="btn btn-sm btn-outline-light" (click)="copy(shareToken)">{{ copiedToken === shareToken ? 'Kopiert' : 'Kopieren' }}</button>
        </div>
        <div class="text-danger mt-2" *ngIf="errShare">{{ errShare }}</div>
      </div>
    </div>
  </div>

  <div class="col-12 col-lg-6">
    <div class="card h-100">
      <div class="card-header d-flex align-items-center gap-2"><strong>📤 Meine Freigaben</strong><span *ngIf="loadingShares" class="spinner-border spinner-border-sm ms-1"></span></div>
      <div class="card-body">
        <div class="alert alert-info" *ngIf="!loadingShares && shares.length === 0">Keine Freigaben vorhanden.</div>
        <div *ngIf="shares.length">
          <div class="tile" *ngFor="let s of shares">
            <div class="d-flex align-items-center gap-2">
              <img *ngIf="festivalById(s.festivalId)?.imageUrl" [src]="festivalById(s.festivalId)?.imageUrl" style="height:32px;width:32px;object-fit:cover;border-radius:6px;">
              <div>
                <div>{{ festivalById(s.festivalId)?.name || s.festivalId }}</div>
                <div class="small muted d-flex align-items-center gap-2">
                  <code>{{ s.aliasOrToken }}</code>
                  <span class="badge text-bg-secondary" *ngIf="s.includeNotes">inkl. Notizen</span>
                </div>
              </div>
            </div>
            <button class="btn btn-sm btn-outline-danger" (click)="revoke(s)" title="Freigabe löschen" aria-label="Freigabe löschen">
              <i class="bi bi-trash"></i>
              <span class="visually-hidden">Löschen</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="col-12">
    <div class="card">
      <div class="card-header d-flex align-items-center justify-content-between">
        <strong>🏷️ Eigene Bewertungsbezeichner</strong>
        <span class="small text-muted">Reihenfolge von 1 (schlecht) bis 5 (sehr gut)</span>
      </div>
      <div class="card-body">
        <div class="labels-list">
          <div class="labels-item" *ngFor="let i of [0,1,2,3,4]">
            <span class="labels-num">{{ i+1 }}</span>
            <input class="form-control" [(ngModel)]="userLabels[i]">
            <button type="button" class="btn btn-sm btn-outline-secondary" (click)="resetLabel(i)" [disabled]="userLabels[i] === defaultUserLabels[i]" title="Auf Standard zurücksetzen" aria-label="Auf Standard zurücksetzen">
              ↺
              <span class="visually-hidden">Zurücksetzen</span>
            </button>
          </div>
        </div>
        <div class="d-flex align-items-center gap-2 mt-3">
          <button class="btn btn-sm btn-success" (click)="saveUserLabels()" [disabled]="savingLabels">
            <span class="spinner-border spinner-border-sm me-1" *ngIf="savingLabels"></span>Speichern
          </button>
        </div>
      </div>
    </div>
  </div>
</div>
  `
})
export class SettingsComponent implements OnInit {
  festivals: any[] = [];
  festivalId = '';
  alias = '';
  includeNotes = true;
  shareToken = '';
  errShare = '';

  shares: any[] = [];
  userLabels: string[] = ['geht gar nicht', 'eher nicht', 'weiß ich noch nicht', 'kann man mal hören', 'unbedingt anhören'];
  readonly defaultUserLabels: string[] = ['geht gar nicht', 'eher nicht', 'weiß ich noch nicht', 'kann man mal hören', 'unbedingt anhören'];
  okLabels = false;
  errLabels = '';
  copiedToken = '';
  private copyTimer?: any;
  loadingShares = false;
  savingLabels = false;

  constructor(private api: ApiService, private location: Location, private route: ActivatedRoute, private toasts: ToastService) {}

  async ngOnInit() {
    const fid = this.route.snapshot.queryParamMap.get('festivalId');
    if (fid) this.festivalId = fid;
    await this.reloadFestivals();
    await this.reloadShares();
    await this.loadUserLabels();
  }

  goBack() { this.location.back(); }

  copy(text: string) {
    try {
      navigator.clipboard.writeText(text);
      this.copiedToken = text;
      clearTimeout(this.copyTimer);
      this.copyTimer = setTimeout(() => { this.copiedToken = ''; }, 1500);
    } catch {
      // Fallback: select+execCommand is deprecated, omit for simplicity
    }
  }

  private async reloadFestivals() {
    try {
      this.festivals = await this.api.listFestivals();
      if (this.festivals.length && !this.festivalId) this.festivalId = this.festivals[0].id;
    } catch {
      this.festivals = [];
    }
  }

  async createShare() {
    try {
      this.errShare = '';
      const res = await this.api.shareCreate(this.festivalId, this.alias || undefined, this.includeNotes);
      this.shareToken = res.aliasOrToken;
      await this.reloadShares();
      this.toasts.success('Freigabe erstellt.');
    } catch (e: any) {
      const msg = String(e?.message || '');
      if (msg.includes('Maximal 4 Freigaben pro Festival erlaubt')) {
        this.toasts.error('Maximal 4 Freigaben pro Festival erlaubt.');
      } else {
        this.toasts.error('Fehler beim Erstellen der Freigabe');
      }
    }
  }

  festivalById(id: string) { return this.festivals.find(f => f.id === id); }

  async reloadShares() {
    try {
      this.loadingShares = true;
      this.shares = await this.api.shareList();
    } catch { this.shares = []; }
    finally { this.loadingShares = false; }
  }

  get festivalSharesCount() {
    return this.shares.filter(s => s.festivalId === this.festivalId).length;
  }

  async revoke(s: any) {
    await this.api.shareDelete(s.festivalId, s.aliasOrToken);
    await this.reloadShares();
    this.toasts.info('Freigabe widerrufen.');
  }

  async loadUserLabels() {
    try {
      const res = await this.api.userSettingsGet();
      if (Array.isArray(res?.labels) && res.labels.length === 5) this.userLabels = res.labels;
    } catch {}
  }

  async saveUserLabels() {
    try {
      this.okLabels = false; this.errLabels = ''; this.savingLabels = true;
      if (this.userLabels.length !== 5) throw new Error('Es müssen 5 Bezeichner gesetzt sein.');
      await this.api.userSettingsPut(this.userLabels);
      this.okLabels = true; this.toasts.success('Labels gespeichert.');
    } catch (e: any) {
      this.errLabels = e?.message || 'Speichern fehlgeschlagen';
      this.toasts.error(this.errLabels);
    }
    finally { this.savingLabels = false; }
  }

  defaultLabel(i: number): string {
    return this.defaultUserLabels[i] || '';
  }

  resetLabel(i: number) {
    this.userLabels[i] = this.defaultUserLabels[i] || '';
  }
}
